import apiClient from '@/api/client';

// --- Analytics Query ---
export interface AnalyticsMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AnalyticsRequest {
  query: string;
  conversation_history: AnalyticsMessage[];
  year?: number;
}

export interface AnalyticsResponse {
  reply: string;
  model: string;
  tokens_used: number;
  input_tokens: number;
  output_tokens: number;
  tools_called: string[];
  data_tier: string;
  duration_ms: number;
}

// --- Tool Definitions ---
export interface ToolDefinition {
  id: string;
  key: string;
  name: string;
  description: string;
  module: string;
  category: string;
  data_tier: string;
  is_active: boolean;
  sort_order: number;
}

export interface ToolToggleRequest {
  tool_id: string;
  is_enabled: boolean;
}

// --- Feature Config ---
export interface FeatureConfig {
  id: string;
  group_id: string;
  company_id: string | null;
  feature_key: string;
  is_enabled: boolean;
  config: Record<string, unknown> | null;
}

export interface FeatureConfigUpdate {
  feature_key: string;
  is_enabled: boolean;
  config?: Record<string, unknown>;
}

// --- Usage ---
export interface UsageSummary {
  total_queries: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  avg_duration_ms: number;
  top_tools: Array<{ tool: string; count: number }>;
  daily_usage: Array<{ date: string; queries: number; tokens: number }>;
}

// --- Page Help ---
export interface PageHelpContent {
  page_key: string;
  title: string;
  content: string;
  related_pages: Array<{ key: string; label: string }> | null;
}

// --- Reminders ---
export interface Reminder {
  id: string;
  title: string;
  description: string | null;
  remind_at: string;
  is_recurring: boolean;
  recurrence_rule: string | null;
  recurrence_end: string | null;
  is_completed: boolean;
  completed_at: string | null;
  created_at: string;
}

export interface ReminderCreate {
  title: string;
  description?: string;
  remind_at: string;
  is_recurring?: boolean;
  recurrence_rule?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  recurrence_end?: string;
}

export const aiAnalyticsApi = {
  // Analytics
  query: (data: AnalyticsRequest) =>
    apiClient.post<AnalyticsResponse>('/ai/analytics/query', data),

  // Tools
  listTools: (module?: string) =>
    apiClient.get<ToolDefinition[]>('/ai/analytics/tools', { params: module ? { module } : {} }),
  toggleTool: (data: ToolToggleRequest) =>
    apiClient.post('/ai/analytics/tools/toggle', data),
  seedTools: () =>
    apiClient.post('/ai/analytics/tools/seed'),

  // Features
  listFeatures: () =>
    apiClient.get<FeatureConfig[]>('/ai/analytics/features'),
  updateFeature: (data: FeatureConfigUpdate) =>
    apiClient.put('/ai/analytics/features', data),

  // Usage
  getUsage: (days: number = 30) =>
    apiClient.get<UsageSummary>('/ai/analytics/usage', { params: { days } }),

  // Page Help
  getPageHelp: (pageKey: string, language: string = 'en') =>
    apiClient.get<PageHelpContent>(`/ai/analytics/page-help/${pageKey}`, { params: { language } }),

  // Reminders
  listReminders: (includeCompleted: boolean = false) =>
    apiClient.get<Reminder[]>('/ai/analytics/reminders', { params: { include_completed: includeCompleted } }),
  createReminder: (data: ReminderCreate) =>
    apiClient.post<Reminder>('/ai/analytics/reminders', data),
  updateReminder: (id: string, data: Partial<ReminderCreate & { is_completed: boolean }>) =>
    apiClient.patch<Reminder>(`/ai/analytics/reminders/${id}`, data),
  deleteReminder: (id: string) =>
    apiClient.delete(`/ai/analytics/reminders/${id}`),
};
